'use client';

import { Box, Text, Flex, Checkbox } from '@radix-ui/themes';
import * as Form from '@radix-ui/react-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState, useRef, useEffect } from 'react';
import type { ComponentType, ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useDebouncedCallback } from 'use-debounce';
import {
  trpcConsumerCompleteOnlineBooking,
  trpcConsumerWebsiteSubmitContact,
} from '@trashlab/api/src/generated/hooks';
import { TLSiteCartData } from '@trashlab/api/src/domains/consumer/types';
import validations from '@trashlab/api/src/domains/consumer/validations/sitesapp/index';
import { AddressPicker } from '@trashlab/sitesapp/src/components/shared/address-picker';
import PaymentForm, {
  PaymentFormHandle,
} from '@trashlab/sitesapp/src/components/payments/payment-form';
import { useServiceAgreementModal } from '@trashlab/sitesapp/src/components/shop/modals/service-agreement-modal';
import { PhoneNumberInput } from '@trashlab/sitesapp/src/components/shared/phone-number-input';
import { FormInput } from '@trashlab/sitesapp/src/templates/default/components/shared/form-input';
import { getClientIp } from '@trashlab/api/src/utils/get-client-ip';
import { CheckoutContactVerification } from '@trashlab/sitesapp/src/components/shop/checkout-contact-verification';
import { CheckoutCustomerAccountSelection } from '@trashlab/sitesapp/src/components/shop/checkout-customer-account-selection';
import { redirectToThankYouUrl } from '@trashlab/sitesapp/src/components/shop/thank-you-redirect-navigation';
import {
  initializeLeadTracking,
  type LeadTrackingData,
} from '@trashlab/sitesapp/src/components/shop/lead-tracking-injector';
import {
  getCheckoutErrorMessage,
  shouldSuggestDifferentCheckoutPaymentMethod,
} from '@trashlab/api/src/domains/consumer/utils/checkout-error-message';
import {
  CHECKOUT_SITE_ADDRESS_ERROR,
  isValidCheckoutSiteAddress,
} from '@trashlab/api/src/domains/consumer/utils/checkout-site-address';

type ShopStepFrameComponent = ComponentType<{
  children: ReactNode;
  cartId: string;
  onNext?: () => Promise<void>;
  canContinue?: boolean;
  isLoading?: boolean;
}>;

export const SitesPaymentStepBase = ({
  cartData,
  ShopStepFrame,
}: {
  cartData: TLSiteCartData;
  ShopStepFrame: ShopStepFrameComponent;
}) => {
  const [isServiceAgreementSigned, setIsServiceAgreementSigned] =
    useState(false);
  const [isPaymentFormValid, setIsPaymentFormValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [clientIp, setClientIp] = useState<string>();
  const [useSameAddress, setUseSameAddress] = useState(false);
  const [contactSaveRevision, setContactSaveRevision] = useState(0);
  const [verificationRevision, setVerificationRevision] = useState(0);
  const [isCustomerSelectionReady, setIsCustomerSelectionReady] =
    useState(false);
  const paymentFormRef = useRef<PaymentFormHandle>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const onlineOrderCheckout = trpcConsumerCompleteOnlineBooking.useMutation();
  const submitContact = trpcConsumerWebsiteSubmitContact.useMutation({
    onSuccess: () => ({ skipInvalidation: true }),
  });

  const form = useForm({
    defaultValues: {
      cartId: cartData.cart.id,
      tenantId: cartData.cart.tenantId,
      customerName: cartData.cart.customerName ?? '',
      customerPhoneNumber: cartData.cart.customerPhoneNumber ?? '',
      customerEmail: cartData.cart.customerEmail ?? '',
      customerAddress: cartData.cart.customerAddress ?? {
        streetAddressPartOne: '',
        streetAddressPartTwo: '',
        city: '',
        state: '',
        postalCode: '',
      },
    },
    resolver: zodResolver(validations.submitContact),
    mode: 'onChange',
  });

  useEffect(() => {
    getClientIp().then((ip) => setClientIp(ip));
  }, []);

  // Copy site address to billing address when checkbox is checked
  useEffect(() => {
    if (useSameAddress && cartData.cart.siteAddress) {
      const siteAddr = cartData.cart.siteAddress;
      form.setValue(
        'customerAddress.streetAddressPartOne',
        siteAddr.streetAddressPartOne || '',
      );
      form.setValue(
        'customerAddress.streetAddressPartTwo',
        siteAddr.streetAddressPartTwo || '',
      );
      form.setValue('customerAddress.city', siteAddr.city || '');
      form.setValue('customerAddress.state', siteAddr.state || '');
      form.setValue('customerAddress.postalCode', siteAddr.postalCode || '');
    }
  }, [useSameAddress, cartData.cart.siteAddress]);

  const getContactSignature = (contactValues: unknown) =>
    JSON.stringify(contactValues);

  // The form starts from the persisted cart, so an unchanged identity click
  // does not need to save the same contact again.
  const lastSavedContactRef = useRef<string>(
    getContactSignature(form.getValues()),
  );
  const contactSaveQueueRef = useRef<Promise<void>>(Promise.resolve());

  const saveCurrentContact = async (contactValues = form.getValues()) => {
    const contactSnapshot = {
      ...contactValues,
      customerAddress: { ...contactValues.customerAddress },
    };
    const contactSignature = getContactSignature(contactSnapshot);

    if (contactSignature === lastSavedContactRef.current) {
      return;
    }

    const savePromise = contactSaveQueueRef.current
      .catch(() => undefined)
      .then(async () => {
        if (contactSignature === lastSavedContactRef.current) {
          return;
        }

        await submitContact.mutateAsync({
          ...contactSnapshot,
          tenantId: cartData.cart.tenantId,
        });

        lastSavedContactRef.current = contactSignature;
        setContactSaveRevision((revision) => revision + 1);
      });

    contactSaveQueueRef.current = savePromise.catch(() => undefined);

    await savePromise;
  };

  const watchedName = form.watch('customerName');
  const watchedEmail = form.watch('customerEmail');
  const watchedPhone = form.watch('customerPhoneNumber');

  const debouncedSaveContact = useDebouncedCallback(async () => {
    const contactValues = form.getValues();

    if (!contactValues.customerEmail && !contactValues.customerPhoneNumber) {
      return;
    }

    const contactSignature = getContactSignature(contactValues);

    if (contactSignature === lastSavedContactRef.current) {
      return;
    }

    try {
      await saveCurrentContact();
    } catch {
      // Silently fail - the contact will be saved on explicit submit anyway.
    }
  }, 1500);

  useEffect(() => {
    if (isSubmitting) {
      return;
    }

    debouncedSaveContact();
  }, [
    isSubmitting,
    watchedName,
    watchedEmail,
    watchedPhone,
    debouncedSaveContact,
  ]);

  const { ServiceAgreementModal, openModal } =
    useServiceAgreementModal(cartData);

  const openTermsAndConditions = () => {
    openModal(() => setIsServiceAgreementSigned(true));
  };

  const isSiteAddressValid = isValidCheckoutSiteAddress(
    cartData.cart.siteAddress,
  );

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setCheckoutError(null);

      if (!isSiteAddressValid) {
        setCheckoutError(CHECKOUT_SITE_ADDRESS_ERROR);
        return;
      }

      const contactValues = form.getValues();
      debouncedSaveContact.cancel();
      await saveCurrentContact(contactValues);

      if (paymentFormRef.current) {
        const result = await paymentFormRef.current.submit();

        const checkoutResult = await onlineOrderCheckout.mutateAsync({
          cartId: cartData.cart.id,
          tenantId: cartData.cart.tenantId,
          isWebsite: true,
          paymentMethodId: result.paymentMethodId,
          ipAddress: clientIp,
        });

        const orderNumber =
          checkoutResult && 'orderNumber' in checkoutResult
            ? (checkoutResult.orderNumber as string | null)
            : null;
        const orderId =
          checkoutResult && 'orderId' in checkoutResult
            ? (checkoutResult.orderId as string | null)
            : null;

        if (formRef.current) {
          const submitEvent = new Event('submit', {
            bubbles: true,
            cancelable: true,
          });
          formRef.current.dispatchEvent(submitEvent);
        }

        if (typeof window !== 'undefined' && (window as any).analytics) {
          (window as any).analytics.trackConversion('shop_checkout_completed', {
            cartId: cartData.cart.id,
            orderId: result.paymentMethodId,
            formId: 'shop-checkout-form',
            formType: 'checkout',
          });
        }

        const siteAddress = cartData.cart.siteAddress as {
          streetAddressPartOne?: string;
          city?: string;
          state?: string;
        } | null;
        const deliveryLocation =
          siteAddress?.streetAddressPartOne &&
          siteAddress?.city &&
          siteAddress?.state
            ? `${siteAddress.streetAddressPartOne}, ${siteAddress.city}, ${siteAddress.state}`
            : null;

        const trackingData: LeadTrackingData = {
          cartId: cartData.cart.id,
          customer: contactValues.customerName || null,
          email: contactValues.customerEmail || null,
          phone: contactValues.customerPhoneNumber || null,
          containerSize: cartData.cart.sku?.name || null,
          orderNumber,
          orderId,
          amount: null,
          deliveryDate: cartData.cart.deliveryServiceDate
            ? new Date(cartData.cart.deliveryServiceDate)
                .toISOString()
                .split('T')[0]
            : null,
          deliveryLocation,
          timestamp: new Date().toISOString(),
        };
        initializeLeadTracking(trackingData);

        const thankyouUrl = new URL(
          `/api/shop/shop-v3/cart/${cartData.cart.id}/thankyou`,
          window.location.origin,
        );
        redirectToThankYouUrl({
          url: thankyouUrl.toString(),
          windowRef: window,
          hasCustomThankYou: Boolean(
            cartData.cart.organization.onlineOrderThankYouPageUrl,
          ),
        });
      }
    } catch (error) {
      console.error('Error during checkout:', error);
      setCheckoutError(getCheckoutErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const checkoutErrorMessage =
    checkoutError ?? (isSiteAddressValid ? null : CHECKOUT_SITE_ADDRESS_ERROR);
  const isPaymentError =
    shouldSuggestDifferentCheckoutPaymentMethod(checkoutErrorMessage);
  const canContinue =
    form.formState.isValid &&
    isServiceAgreementSigned &&
    isPaymentFormValid &&
    isSiteAddressValid &&
    isCustomerSelectionReady &&
    !isSubmitting;

  return (
    <ShopStepFrame
      cartId={cartData.cart.id}
      onNext={handleSubmit}
      canContinue={canContinue}
      isLoading={isSubmitting}
    >
      <Form.Root
        ref={formRef}
        id="shop-checkout-form"
        onSubmit={handleSubmit}
        className="w-full"
      >
        <Flex direction="column" gap="2" my="2">
          <Text size="4" weight="bold">
            Service contact information
          </Text>

          <FormInput
            {...form.register('customerName')}
            name="customerName"
            id="customerName"
            label="Site contact name"
            placeholder="Your name"
            required
            error={form.formState.errors.customerName?.message?.toString()}
          />

          <FormInput
            {...form.register('customerEmail')}
            name="customerEmail"
            id="customerEmail"
            label="Email"
            placeholder="Your email address"
            type="email"
            required
            error={form.formState.errors.customerEmail?.message?.toString()}
          />

          <PhoneNumberInput
            value={watchedPhone}
            onChange={(value: string) => {
              form.setValue('customerPhoneNumber', value, {
                shouldValidate: true,
              });
            }}
            error={form.formState.errors.customerPhoneNumber?.message?.toString()}
            label="Phone number"
            required
          />

          <CheckoutContactVerification
            cartId={cartData.cart.id}
            tenantId={cartData.cart.tenantId}
            emailValue={watchedEmail}
            phoneValue={watchedPhone}
            savedContactRevision={contactSaveRevision}
            onBeforeSend={saveCurrentContact}
            onVerified={() =>
              setVerificationRevision((revision) => revision + 1)
            }
          />

          <CheckoutCustomerAccountSelection
            cartId={cartData.cart.id}
            tenantId={cartData.cart.tenantId}
            savedContactRevision={contactSaveRevision}
            verificationRevision={verificationRevision}
            currentSiteAddress={cartData.cart.siteAddress}
            onBeforeSelect={saveCurrentContact}
            onSelectionReadyChange={setIsCustomerSelectionReady}
          />

          <Text size="4" mt="2" weight="bold">
            Billing information
          </Text>
          <Text>All transactions are secure and encrypted.</Text>

          {cartData.cart.siteAddress && (
            <label className="flex items-center gap-2 cursor-pointer my-2">
              <Checkbox
                checked={useSameAddress}
                onCheckedChange={(checked) =>
                  setUseSameAddress(checked === true)
                }
              />
              <Text size="2">Use same address as delivery location</Text>
            </label>
          )}

          {!useSameAddress && (
            <Box style={{ position: 'relative', zIndex: 20 }}>
              <AddressPicker
                prefix="customerAddress"
                form={form}
                hideMap
                customInitLabel="Billing address"
                id="customerAddress"
              />
            </Box>
          )}

          {useSameAddress && cartData.cart.siteAddress && (
            <Box className="p-3 bg-gray-50 rounded-md border border-gray-200">
              <Text size="2" color="gray">
                {cartData.cart.siteAddress.streetAddressPartOne}
                {cartData.cart.siteAddress.streetAddressPartTwo &&
                  `, ${cartData.cart.siteAddress.streetAddressPartTwo}`}
                , {cartData.cart.siteAddress.city},{' '}
                {cartData.cart.siteAddress.state}{' '}
                {cartData.cart.siteAddress.postalCode}
              </Text>
            </Box>
          )}

          <PaymentForm
            cartId={cartData.cart.id}
            allowedPaymentMethodTypes={['card']}
            organization={cartData.cart.organization}
            onValidationChange={setIsPaymentFormValid}
            ref={paymentFormRef}
            isInvalid={
              !form.formState.isValid ||
              !isServiceAgreementSigned ||
              !isSiteAddressValid ||
              !isCustomerSelectionReady
            }
          />

          {checkoutErrorMessage && (
            <Box className="my-3 rounded-md border border-red-300 bg-red-50 p-3">
              <Text size="2" color="red" weight="medium">
                {checkoutErrorMessage}
              </Text>
              {isPaymentError && (
                <Text size="1" color="gray" className="mt-1 block">
                  Please try again with a different payment method.
                </Text>
              )}
            </Box>
          )}

          <Box className="my-4">
            <label
              htmlFor="termsAccepted"
              className="flex items-center gap-2 cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                openTermsAndConditions();
              }}
            >
              <input
                type="checkbox"
                id="termsAccepted"
                checked={isServiceAgreementSigned}
                className="h-4 w-4 cursor-pointer"
              />
              <Text>
                I accept the{' '}
                <span className="text-[var(--accent-9)]">
                  Terms and Conditions
                </span>{' '}
                and{' '}
                <span className="text-[var(--accent-9)]">Privacy Policy</span>
              </Text>
            </label>
          </Box>
        </Flex>
      </Form.Root>

      {ServiceAgreementModal}
    </ShopStepFrame>
  );
};
